import React, { ReactNode, useContext, useEffect, useState } from 'react';
import Spinner from '../../Spinner';
import { GlobalErrorContext } from '../../../contexts/GlobalError';
import classNames from 'classnames';

import { ReactComponent as Error } from 'icons/error.svg';
import http from 'components/Profile/http';
import { usePrescriptionContext } from './PrescriptioWasSentContext';

type UploadAndSendPrescriptionButtonProps = {
  handleRefetchAppointments: () => void;
  numberOfPrescriptions: number;
  pharmacyId: number | null;
  appointmentId: number;
};

type State = {
  buttonContent: ReactNode;
  buttonClass: string;
  helpText: string | undefined;
  disabled: boolean;
};

type StateDictionary = {
  generalError: State;
  ready: State;
  prescriptionSent: State;
  loading: State;
  noPrescriptions: State;
};

const stateMap: StateDictionary = {
  ready: {
    buttonContent: 'Rezept hochladen',
    buttonClass: 'text-white bg-swopa-secondary-light-blue hover:bg-[#2B81EC]',
    helpText: undefined,
    disabled: false,
  },
  loading: {
    buttonContent: <Spinner fill="#ffffff" height="18px" width="18px" />,
    buttonClass: 'text-white bg-swopa-secondary-light-blue cursor-wait',
    helpText: 'Rezept in die Apotheke hochladen. Bitte warten Sie.',
    disabled: true,
  },
  prescriptionSent: {
    buttonContent: 'Rezept hochgeladen',
    buttonClass: 'bg-swopa-accent-green/50 cursor-help',
    helpText: 'Rezept hochgeladen.',
    disabled: true,
  },
  generalError: {
    buttonContent: <Error height="18px" width="18px" />,
    buttonClass: 'text-white cursor-help bg-swopa-warning-red/50',
    helpText:
      'Rezept kann nicht hochgeladen werden. Versuchen Sie es erneut oder kontaktieren Sie den Administrator..',
    disabled: true,
  },
  noPrescriptions: {
    buttonContent: 'Rezept hochladen',
    buttonClass: 'text-white bg-swopa-secondary-light-blue cursor-help',
    helpText: 'Nicht hinzugefügte Verschreibungen.',
    disabled: true,
  },
};

function UploadAndSendPrescriptionButton({
  handleRefetchAppointments,
  numberOfPrescriptions,
  pharmacyId,
  appointmentId,
}: UploadAndSendPrescriptionButtonProps) {
  const [buttonState, setButtonState] = useState<State>(stateMap.ready);
  const { setError } = useContext(GlobalErrorContext);

  const {
    wasPrescriptionSent,
    setWasPrescriptionSent,
  } = usePrescriptionContext();

  useEffect(() => {
    if (numberOfPrescriptions == 0) {
      setButtonState(stateMap.noPrescriptions);
    }

    if (wasPrescriptionSent) {
      setButtonState(stateMap.prescriptionSent);
    }
  }, [numberOfPrescriptions, wasPrescriptionSent]);

  const handleGeneralError = () => {
    setButtonState(stateMap.generalError);
    setTimeout(() => setButtonState(stateMap.ready), 5000);
    setError('Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
  };

  const upload = () => {
    setButtonState(stateMap.loading);
    http
      .post(`/medical/pharmacies/${pharmacyId}/upload/`, {
        appointment: appointmentId,
      })
      .then((res) => {
        if (res.status === 200) {
          setButtonState(stateMap.prescriptionSent);
          handleRefetchAppointments();
          setWasPrescriptionSent(true);
        } else {
          handleGeneralError();
        }
      })
      .catch(() => {
        handleGeneralError();
      });
  };

  return (
    <button
      type="button"
      title={buttonState.helpText}
      className={classNames(
        'w-full h-full p-3 rounded-[4px] font-radikal text-xs uppercase flex justify-center items-center disabled:opacity-50 hover:ease-in-out',
        buttonState.buttonClass,
      )}
      onClick={upload}
      disabled={buttonState.disabled}
    >
      {buttonState.buttonContent}
    </button>
  );
}

export default UploadAndSendPrescriptionButton;
